import { Component, HostBinding, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { LssDeploymentEntity } from '../store/deployment.datamodel';
import { ActorObject, ActorType } from '../../shared/datamodel';
import { DeploymentService } from '../store/deployment.service';
import { ORG_OGU_COMPONENTS_TYPES } from '../../publish/component/store/component.datamodel';
import { AppService } from '../../application/app.service';

@Component({
    selector: 'ama-ng-app-store-deployment-hierarchy-list',
    templateUrl: './deployment-hierarchy-list.component.html',
    styleUrls: ['./deployment-hierarchy-list.component.scss'],
    standalone: false
})
export class DeploymentHierarchyListComponent implements OnDestroy {
  @Input() currentItem: LssDeploymentEntity | undefined;
  @Input() items: LssDeploymentEntity[] = [];
  @Input() @HostBinding("style.--depth")depth = 0;
  @Input() hierarchyNodeId: string | undefined;

  subscription: Subscription = new Subscription();
  appId = '';
  loadedORGs: ActorObject[] = [];
  selectedActor: ActorObject | undefined;
  canDeployOrgOguLevels = false;
  indeterminateDeploymentActors: Set<string> = new Set<string>();
  indeterminateExclusionActors: Set<string> = new Set<string>();
  protected readonly LssDeploymentEntity = LssDeploymentEntity;
  protected readonly ActorType = ActorType;

  constructor(protected readonly deploymentService: DeploymentService,
              private readonly appService: AppService) {
    this.subscription.add(deploymentService.selectedEntity$.subscribe(
      selectedEntity => {
        this.selectedActor = selectedEntity;
      }
    ));
    this.subscription.add(this.appService.detailedApp$.subscribe(app => {
      this.appId = app.id ?? '';
      this.canDeployOrgOguLevels = ORG_OGU_COMPONENTS_TYPES.includes(
        this.appService.getAppComponentsType(app.components, app.applicationType)
      );
    }));
    this.subscription.add(deploymentService.selectIndeterminateActors$.subscribe(({
      indeterminateDeploymentActors,
      indeterminateExclusionActors
    }) => {
      this.indeterminateDeploymentActors = indeterminateDeploymentActors;
      this.indeterminateExclusionActors = indeterminateExclusionActors;
    }));
    this.subscription.add(this.deploymentService.loadedORGs$.subscribe(loadedORGs => {
          this.loadedORGs = loadedORGs;
        }
      )
    );
  }

  /**
   * Dispatch the selected entity and load the hierarchy if needed
   * @param entity LSS entity
   */
  selectEntity(entity: LssDeploymentEntity): void {
    if (this.appId !== '' && entity && entity.actor.actorType === ActorType.ORGANISATION &&
      //We have to ensure that the ORG is not already loaded, but we have to wait for the selected entity to be loaded
      this.loadedORGs.length > 0 && !this.loadedORGs.some(org => org.actorName === entity.actor.actorName)) {
      const loadHierarchy = entity.childrenRef.length === 0;
      this.appService.appRetrieveDeploymentAndConfigurationsByOrg(this.appId, entity.actor.actorName, loadHierarchy);
    }
    this.deploymentService.dispatchSelectEntity(entity.actor);
  }

  /**
   * Dispatch toggle entity collapsed state action and stop event propagation
   * @param entity LSS entity
   */
  collapse(entity: LssDeploymentEntity) {
    if (this.canDeployOrgOguLevels) {
      this.deploymentService.dispatchToggleEntityCollapsedState(entity.actor);
    }
  }

  /**
   * Track by function for ngFor
   * @param _index Item index
   * @param entity LSS entity
   */
  trackFn(_index: number, entity: LssDeploymentEntity): string {
    return `${entity.actor.actorType}.${entity.actor.actorName}`;
  }

  /**
   * Dispatch toggle entity deployment state action and stop event propagation
   * @param entity LSS entity
   */
  deploy(entity: LssDeploymentEntity) {
    if (entity.isExcluded) {
      this.deploymentService.dispatchToggleEntityExclusionState(entity.actor);
    }
    this.deploymentService.dispatchToggleEntityDeploymentState(entity.actor);
  }

  /**
   * Dispatch toggle entity exclusion state action and stop event propagation
   * @param entity LSS entity
   */
  exclude(entity: LssDeploymentEntity) {
    this.deploymentService.dispatchToggleEntityExclusionState(entity.actor);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
